import { cur } from '@/lib/currency';
import { Fragment, useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { BranchFilter } from '@/components/BranchFilter';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const COLORS = ['#7C3AED', '#0EA5E9', '#22C55E', '#F59E0B', '#EF4444', '#EC4899', '#8B5CF6'];

export default function CategoryReportPage() {
  const { t } = useLanguage();
  const [reportData, setReportData] = useState([]);
  const f = useReportFilters({ withUnassigned: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [f.params]);

  const fetchReport = async () => {
    try {
      const response = await api.get('/reports/supplier-categories', { params: f.params });
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch category report');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !reportData.length) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>);
  }

  const allRows = Array.isArray(reportData) ? reportData : [];
  const safeData = allRows.filter((c) => c.class !== 'repayment');
  const repayments = allRows.filter((c) => c.class === 'repayment');
  const totalExpenses = safeData.reduce((sum, c) => sum + (c.total_expenses || 0), 0);
  const totalPaid = safeData.reduce((sum, c) => sum + (c.total_paid || 0), 0);
  const totalCredit = safeData.reduce((sum, c) => sum + (c.total_credit || 0), 0);
  const groups = Object.values(safeData.reduce((acc, c) => {
    const g = acc[c.group || 'Other expenses'] ||= { name: c.group || 'Other expenses', rows: [], Expenses: 0, Paid: 0, Credit: 0 };
    g.rows.push(c); g.Expenses += c.total_expenses || 0; g.Paid += c.total_paid || 0; g.Credit += c.total_credit || 0;
    return acc;
  }, {})).sort((a, b) => b.Expenses - a.Expenses);

  const barData = groups.filter(g => g.Expenses > 0 || g.Paid > 0);

  const pieData = groups.filter(g => g.Expenses > 0).map(g => ({ name: g.name, value: g.Expenses }));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <ReportHeader icon={BarChart3} title={<span data-testid="category-report-title">Category Report</span>} subtitle="Supplier category breakdown with charts" testId="category-header" />
        <ReportFilterBar testId="category-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds} range={f.range} onRange={f.setRange}
          exportDisabled={!safeData.length} exportSpec={() => ({
            title: 'Category Report', subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'category_report', rows: allRows,
            cols: [{ key: 'group', label: 'Group' }, { key: 'category', label: 'Category' }, { key: 'class', label: 'Type' }, { key: 'supplier_count', label: 'Suppliers', numeric: true }, { key: 'total_expenses', label: 'Amount', numeric: true },
              { key: 'total_paid', label: 'Paid', numeric: true }, { key: 'total_credit', label: 'Credit', numeric: true }],
          })} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Category Expenses</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold font-outfit text-error"> {cur()} {totalExpenses.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Paid</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold font-outfit text-success"> {cur()} {totalPaid.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Outstanding Credit</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold font-outfit text-warning"> {cur()} {totalCredit.toFixed(2)}</div></CardContent></Card>
        </div>

        {barData.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border">
              <CardHeader><CardTitle className="font-outfit text-base">Category-wise Comparison</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${cur()} ${Number(v).toFixed(2)}`} />
                    <Legend />
                    <Bar dataKey="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Paid" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Credit" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader><CardTitle className="font-outfit text-base">Expense Distribution</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${cur()} ${Number(v).toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-border">
          <CardHeader><CardTitle className="font-outfit">Category Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="category-report-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 font-medium text-sm">Category</th>
                    <th className="text-center p-3 font-medium text-sm">Suppliers</th>
                    <th className="text-right p-3 font-medium text-sm">Total Expenses</th>
                    <th className="text-right p-3 font-medium text-sm">Total Paid</th>
                    <th className="text-right p-3 font-medium text-sm">Outstanding Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <Fragment key={g.name}>
                      <tr className="bg-muted/60 border-b border-border" data-testid={`category-group-${g.name}`}>
                        <td className="p-2.5 text-sm font-bold">{g.name}</td><td />
                        <td className="p-2.5 text-sm text-right font-bold text-error">{cur()} {g.Expenses.toFixed(2)}</td>
                        <td className="p-2.5 text-sm text-right font-bold text-success">{cur()} {g.Paid.toFixed(2)}</td>
                        <td className="p-2.5 text-sm text-right font-bold text-warning">{cur()} {g.Credit.toFixed(2)}</td>
                      </tr>
                      {g.rows.map((cat) => (
                        <tr key={`${g.name}-${cat.category}`} className="border-b border-border hover:bg-secondary/50" data-testid="category-report-row">
                          <td className="p-2.5 pl-6 text-sm"><span className="px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">{cat.category}</span>{cat.supplier_linked && <span className="ml-1 text-[10px] text-muted-foreground">supplier category</span>}</td>
                          <td className="p-2.5 text-center"><Badge variant="secondary">{cat.supplier_count}</Badge></td>
                          <td className="p-2.5 text-sm text-right text-error"> {cur()} {cat.total_expenses.toFixed(2)}</td>
                          <td className="p-2.5 text-sm text-right text-success"> {cur()} {cat.total_paid.toFixed(2)}</td>
                          <td className="p-2.5 text-sm text-right text-warning"> {cur()} {cat.total_credit.toFixed(2)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {repayments.length > 0 && (
                    <tr className="bg-sky-50 border-b border-border" data-testid="category-repayments">
                      <td className="p-2.5 text-sm font-semibold" colSpan={2}>Supplier repayments (settle earlier credit — not an expense, not in totals above)</td>
                      <td className="p-2.5 text-sm text-right" colSpan={3}>{cur()} {repayments.reduce((s, r) => s + (r.total_expenses || 0), 0).toFixed(2)}</td>
                    </tr>
                  )}
                  {allRows.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No category data available</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
